import { z } from 'zod';

const uuid = z.string().uuid();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const money = z.number().finite().min(0).max(1e13);
const opt = <T extends z.ZodTypeAny>(s: T) => z.preprocess((v) => (v === '' ? null : v), s.nullable()).optional();

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const CAMPAIGN_TYPES = [
  { value: 'advertisement', label: 'Advertisement' }, { value: 'email', label: 'Email' }, { value: 'webinar', label: 'Webinar' },
  { value: 'conference', label: 'Conference' }, { value: 'trade_show', label: 'Trade show' }, { value: 'direct_mail', label: 'Direct mail' },
  { value: 'social', label: 'Social' }, { value: 'referral', label: 'Referral program' }, { value: 'other', label: 'Other' },
] as const;
export const CAMPAIGN_STATUSES = [
  { value: 'planned', label: 'Planned' }, { value: 'in_progress', label: 'In progress' }, { value: 'completed', label: 'Completed' }, { value: 'aborted', label: 'Aborted' },
] as const;
/** Campaign hierarchies are limited to five levels. */
export const CAMPAIGN_MAX_DEPTH = 5;
export const CAMPAIGN_MEMBERS_PER_REQUEST = 200;

export const campaignSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    type: z.enum(CAMPAIGN_TYPES.map((t) => t.value) as [string, ...string[]]).default('other'),
    status: z.enum(CAMPAIGN_STATUSES.map((t) => t.value) as [string, ...string[]]).default('planned'),
    parentId: opt(uuid),
    startDate: opt(date),
    endDate: opt(date),
    budgetedCost: opt(money),
    actualCost: opt(money),
    expectedRevenue: opt(money),
    description: opt(z.string().trim().max(32000)),
    isActive: z.boolean().default(true),
    ownerId: uuid.optional(),
  })
  .strict()
  .refine((c) => !c.startDate || !c.endDate || c.endDate >= c.startDate, { path: ['endDate'], message: 'End date must be on or after the start date' });
export type CampaignInput = z.infer<typeof campaignSchema>;

export interface CampaignStats {
  leads: number;
  contacts: number;
  responses: number;
  convertedLeads: number;
  opportunities: number;
  opportunityAmount: number;
  wonOpportunities: number;
  wonAmount: number;
}

export interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  parentId: string | null;
  parentName: string | null;
  startDate: string | null;
  endDate: string | null;
  budgetedCost: number | null;
  actualCost: number | null;
  expectedRevenue: number | null;
  description: string | null;
  isActive: boolean;
  ownerId: string;
  ownerName: string;
  version: number;
  createdAt: string;
}

export interface CampaignDetail {
  campaign: Campaign;
  stats: CampaignStats;
  /** This campaign and every campaign below it. */
  hierarchyStats: CampaignStats;
  children: { id: string; name: string; status: string }[];
  statuses: CampaignMemberStatus[];
  canEdit: boolean;
}

export interface CampaignMemberStatus { id: string; label: string; hasResponded: boolean; isDefault: boolean; sortOrder: number }

export const memberStatusesSchema = z
  .object({
    statuses: z.array(z.object({ id: uuid.optional(), label: z.string().trim().min(1).max(60), hasResponded: z.boolean(), isDefault: z.boolean() }).strict())
      .min(2).max(20)
      .refine((s) => s.filter((x) => x.isDefault).length === 1, 'Mark exactly one status as the default')
      .refine((s) => s.some((x) => x.hasResponded) && s.some((x) => !x.hasResponded), 'Have at least one responded and one not-responded status')
      .refine((s) => new Set(s.map((x) => x.label.toLowerCase())).size === s.length, 'Status labels must be unique'),
  })
  .strict();

export const addMembersSchema = z
  .object({
    leadIds: z.array(uuid).max(CAMPAIGN_MEMBERS_PER_REQUEST).default([]),
    contactIds: z.array(uuid).max(CAMPAIGN_MEMBERS_PER_REQUEST).default([]),
    statusId: uuid.optional(),
  })
  .strict()
  .refine((v) => v.leadIds.length + v.contactIds.length > 0, 'Choose at least one lead or contact')
  .refine((v) => v.leadIds.length + v.contactIds.length <= CAMPAIGN_MEMBERS_PER_REQUEST, `Add up to ${CAMPAIGN_MEMBERS_PER_REQUEST} at a time`);

export interface CampaignMember {
  id: string;
  campaignId: string;
  campaignName: string;
  kind: 'lead' | 'contact';
  recordId: string;
  name: string;
  company: string | null;
  email: string | null;
  statusId: string;
  statusLabel: string;
  hasResponded: boolean;
  firstRespondedAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Pipelines
// ---------------------------------------------------------------------------

export interface Pipeline { id: string; name: string; isDefault: boolean; isActive: boolean; sortOrder: number }

export const pipelineSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    isActive: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    /** On create: copy the stages of this pipeline (otherwise the default pipeline's). */
    copyStagesFrom: uuid.optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Lead queues
// ---------------------------------------------------------------------------

export interface Queue { id: string; name: string; userIds: string[]; isMember: boolean }

export const queueSchema = z.object({ name: z.string().trim().min(1).max(80), userIds: z.array(uuid).max(500).default([]) }).strict();

// ---------------------------------------------------------------------------
// Recurring activities
// ---------------------------------------------------------------------------

export const RECURRENCE_MAX_OCCURRENCES = 52;

export const recurrenceSchema = z
  .object({
    frequency: z.enum(['daily', 'weekly', 'monthly']),
    interval: z.number().int().min(1).max(12).default(1),
    /** Total number of occurrences, including the first (2–52). */
    count: z.number().int().min(2).max(RECURRENCE_MAX_OCCURRENCES),
  })
  .strict();
export type Recurrence = z.infer<typeof recurrenceSchema>;

/**
 * Dates of each occurrence (YYYY-MM-DD), starting with `start`. Monthly repeats keep the day of the
 * month, using the last day when a month is shorter (31 Jan → 28/29 Feb → 31 Mar).
 */
export function occurrenceDates(start: string, r: Recurrence): string[] {
  const [y, m, d] = start.split('-').map(Number) as [number, number, number];
  const out: string[] = [];
  for (let i = 0; i < r.count; i++) {
    let dt: Date;
    if (r.frequency === 'monthly') {
      const target = new Date(Date.UTC(y, m - 1 + i * r.interval, 1));
      const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
      dt = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, last)));
    } else {
      dt = new Date(Date.UTC(y, m - 1, d + i * r.interval * (r.frequency === 'weekly' ? 7 : 1)));
    }
    out.push(dt.toISOString().slice(0, 10));
  }
  return out;
}
